import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, ShieldCheck, Globe2, Lightbulb, Megaphone, Link2, FlaskConical } from "lucide-react";
import Reveal from "../components/Reveal";
import CtaSection from "../components/CtaSection";

const values = [
  { icon: Sparkles, title: "Simplicity", desc: "Complex financial infrastructure should feel simple to use." },
  { icon: ShieldCheck, title: "Trust", desc: "Money movement requires transparency, security and accountability." },
  { icon: Globe2, title: "Global Thinking", desc: "African businesses should be able to operate beyond borders." },
  { icon: Lightbulb, title: "Innovation", desc: "We continuously improve how money moves." },
];

const news = [
  {
    icon: Megaphone,
    date: "August 2026",
    tag: "Company",
    title: "Swiftwire Payments launches its global money movement platform",
    desc: "We're introducing Swiftwire: one connected platform for collections, multi-currency accounts, FX and payouts — built for businesses that operate across borders.",
  },
  {
    icon: Link2,
    date: "July 2026",
    tag: "Product",
    title: "Introducing Payment Links",
    desc: "Get paid without building a checkout. Create a secure payment link, share it through any digital channel, and track every payment in one workspace.",
  },
  {
    icon: FlaskConical,
    date: "July 2026",
    tag: "Developers",
    title: "Developer sandbox announced",
    desc: "A sandbox environment for the Swiftwire APIs is on the way — test collections, payouts, accounts and FX end to end before going live.",
  },
];

const Company = () => (
  <div data-testid="company-page">
    <section className="sw-grid-bg border-b border-[#E2E8F0] bg-white py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2E7CD6]">Company</p>
          <h1 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-[#0F172A] sm:text-5xl lg:text-6xl">
            Making global money movement simpler.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#334155] md:text-lg">
            Swiftwire Payments is building modern infrastructure for businesses that need to move money across African markets and beyond.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="bg-white py-20 md:py-28">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2E7CD6]">Our Mission</p>
          <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-[#0F172A] md:text-5xl md:leading-tight">
            Make moving money as simple as sending information.
          </h2>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="text-base leading-relaxed text-[#334155] md:text-lg">
            Money should move the way modern business moves — across African markets, currencies and borders, without unnecessary friction. We build the connected infrastructure that makes that possible: collections, accounts, FX and payouts, working as one.
          </p>
          <p className="mt-5 text-base leading-relaxed text-[#334155] md:text-lg">
            Everything we design starts from a single idea: money moves through Swiftwire — clearly, quickly and with complete visibility.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="bg-white pb-20 md:pb-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="relative h-[260px] overflow-hidden rounded-3xl border border-[#E2E8F0] shadow-sm md:h-[420px]">
            <img
              src="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1600&h=900&q=80&auto=format&fit=crop&crop=faces"
              alt="Two African women working together at a computer"
              loading="lazy"
              className="h-full w-full object-cover object-[50%_15%]"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0B1120]/70 to-transparent p-6 md:p-8">
              <p className="font-heading text-lg font-bold text-white md:text-xl">Building the future of money movement, together.</p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-[#F8FAFC] py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <h2 className="font-heading text-3xl font-bold tracking-tight text-[#0F172A] md:text-4xl">What we believe.</h2>
        </Reveal>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-sm" data-testid={`value-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EFF5FD] text-[#1E3A6E]">
                  <v.icon size={20} strokeWidth={1.5} />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-[#0F172A]">{v.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-[#64748B]">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section id="news" className="scroll-mt-24 bg-white py-20 md:py-28" data-testid="news-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2E7CD6]">News</p>
          <h2 className="mt-4 font-heading text-3xl font-bold tracking-tight text-[#0F172A] md:text-4xl">The latest from Swiftwire.</h2>
          <p className="mt-4 text-base text-[#334155] md:text-lg">Product updates, company news and what we're building next.</p>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {news.map((n, i) => (
            <Reveal key={n.title} delay={i * 0.08}>
              <article
                data-testid={`news-card-${i + 1}`}
                className="group flex h-full flex-col rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-sm transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-900/[0.06]"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#EFF5FD] text-[#1E3A6E]">
                    <n.icon size={20} strokeWidth={1.5} />
                  </span>
                  <span className="rounded-full bg-[#F1F5F9] px-3 py-1 text-xs font-medium text-[#64748B]">{n.tag}</span>
                </div>
                <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{n.date}</p>
                <h3 className="mt-2 font-heading text-lg font-bold leading-snug text-[#0F172A]">{n.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-[#64748B]">{n.desc}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E3A6E]">
                  Read announcement
                  <ArrowUpRight size={15} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[#F8FAFC] py-20 md:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-8 rounded-3xl border border-[#E2E8F0] bg-white bg-gradient-to-br from-[#F8FAFC] to-[#EFF5FD] p-8 sm:p-12 md:flex-row md:items-center">
            <div>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-[#0F172A] md:text-3xl">Want to build with us?</h2>
              <p className="mt-3 max-w-lg text-base text-[#334155]">
                Tell us what you're building and we'll show you how Swiftwire can help.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="company-contact-cta"
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-[#1E3A6E] px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#16294e]"
            >
              Talk to Swiftwire
              <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>

    <CtaSection />
  </div>
);

export default Company;
